import { Info } from 'lucide-react';
import type { ComponentType, HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';

/** A page section: white, 1px ink border, `rounded-2xl` — vervangt
 * `.dashboard-panel`, `.stat-tile`, and ad-hoc section wrappers. */
export function Card({
  children,
  className,
  padding = 'normal',
  ...rest
}: {
  children?: ReactNode;
  className?: string;
  /** `"none"` for content that manages its own padding (e.g. `DataTable`,
   * which needs edge-to-edge rows). */
  padding?: 'normal' | 'none';
} & Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'children'>) {
  return (
    <div
      className={cn(
        'border-line bg-paper surface-glow rounded-2xl border',
        padding === 'normal' && 'p-6',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/** A `Card`'s heading row: an optional leading icon badge, `h2` + an
 * optional action on the right. */
export function CardHeader({
  title,
  icon: Icon,
  info,
  action,
  className,
}: {
  title: ReactNode;
  /** A `lucide-react` icon component, rendered in the small circular tinted
   * badge to the left of the title (same pattern as the "Hoe gaat het met…"
   * summary card). Omitted entirely by most callers — no icon renders
   * exactly as before, no layout shift. */
  icon?: ComponentType<{ className?: string }>;
  /** Tooltip text for a small info icon next to the title — what the
   * metric/chart means, shown on hover/focus rather than crowding the
   * heading itself. */
  info?: string;
  action?: ReactNode;
  className?: string;
}) {
  const heading = (
    <h2 className="font-heading text-ink flex items-center gap-1.5 text-base font-semibold">
      {title}
      {info && <InfoIcon text={info} />}
    </h2>
  );

  return (
    <div className={cn('mb-4 flex items-center justify-between gap-3', className)}>
      {/* Only wrap the heading in an icon+title group when there's an icon
       * to group it with — an omitted icon keeps the heading a direct child
       * of this row, same DOM shape as before this prop existed, so
       * existing callers (e.g. a test walking up from the heading to its
       * `Card`) see no structural change. */}
      {Icon ? (
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="bg-mint/10 text-mint-deep flex size-9 shrink-0 items-center justify-center rounded-full"
          >
            <Icon className="size-5" />
          </span>
          {heading}
        </div>
      ) : (
        heading
      )}
      {action}
    </div>
  );
}

/** The small circled-info affordance shared by `CardHeader` and `StatTile` —
 * a native `title` tooltip is enough here (no rich content), so no tooltip
 * library is pulled in just for this. */
export function InfoIcon({ text }: { text: string }) {
  return (
    <span title={text} tabIndex={0} className="text-accent inline-flex focus:outline-none">
      <Info className="size-3.5" aria-hidden="true" />
      <span className="sr-only">{text}</span>
    </span>
  );
}
